import React, { useEffect, useRef, useState, useMemo } from "react";
import { Card, CardBody, Col, Container, Row, Button, Input, Label, Spinner } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import SearchableSelect from "../../../Components/Common/SearchableSelect";
import FilterCard from "../../../Components/Common/FilterCard";
import { useLayoutStore } from "../../../stores/useLayoutStore";
import { useFilterData } from "./filter-hook";
import { useAlerteNbreStatQuery } from "../../../hooks/queries/useRoadAlertQueries";
import { Chart } from "chart.js/auto";

// Correspondance idStatutAlerte (API) → libellé affiché
const STATUT_LABELS: Record<string, string> = {
  "1": "Non Affectée",
  "2": "Non Demarrée",
  "3": "En Cours (Pause)",
  "4": "Terminée",
  "5": "Arrêtée",
  "6": "En Cours (Travaux)",
  "7": "Grand Public",
  "8": "Rejeté",
};

const buildStatsPayload = (filters: {
  idCommune: string;
  idPriorite: string;
  idStatutAlerte: string;
  dateDebut: string;
  dateFin: string;
}) => ({
  user: "",
  data: {
    idPriorite: filters.idPriorite || "",
    idCommune: filters.idCommune || "",
    idStatutAlerte: filters.idStatutAlerte || "",
    creePar: "",
    dateCreationParam: {
      operator: "[]",
      start: filters.dateDebut || "",
      end: filters.dateFin || "",
    },
  },
});

const parseStatsData = (rawData: unknown): Record<string, number> => {
  const statsData: Record<string, number> = {};
  if (Array.isArray(rawData)) {
    rawData.forEach((item: { StatutAlerte?: number; nombre_alertes?: number }) => {
      const id = item.StatutAlerte != null ? String(item.StatutAlerte) : "";
      if (id !== "") {
        statsData[id] = Number(item.nombre_alertes) || 0;
      }
    });
  } else if (rawData && typeof rawData === "object" && !Array.isArray(rawData)) {
    Object.assign(statsData, rawData);
  }
  return statsData;
};

const DashboardRoadAlert = () => {
  const layoutModeType = useLayoutStore((s) => s.layoutModeType);
  const isDarkMode = layoutModeType === 'dark';
  const [filters, setFilters] = useState({
    idCommune: "",
    idPriorite: "",
    idStatutAlerte: "",
    dateDebut: "",
    dateFin: ""
  });
  const [searchParams, setSearchParams] = useState<ReturnType<typeof buildStatsPayload> | null>(null);
  const initialLoadDone = useRef(false);

  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  const { options, loading: filtersLoading } = useFilterData();
  const payload = searchParams ?? buildStatsPayload(filters);
  const statsQuery = useAlerteNbreStatQuery(payload, {
    enabled: !filtersLoading,
  });
  const rawData = statsQuery.data;
  const stats = parseStatsData(rawData);
  const loading = statsQuery.isLoading;

  useEffect(() => {
    if (!filtersLoading && !initialLoadDone.current) {
      initialLoadDone.current = true;
      setSearchParams(buildStatsPayload(filters));
    }
  }, [filtersLoading, filters]);

  const loadData = () => {
    setSearchParams(buildStatsPayload(filters));
  };

  // Supprimé: la recherche ne se lance plus automatiquement quand les filtres changent
  // La recherche se lance uniquement via le bouton "RECHERCHER"

  const totalAlertes = Object.values(stats).reduce((a, b) => a + Number(b || 0), 0);

  // Ratio d'affichage : 1000 alertes réelles = 100 sur l'histogramme
  const HISTO_RATIO = 100 / 1000;
  // Utiliser useMemo pour éviter que les dépendances changent à chaque render
  const statutIds = useMemo(() => ["1", "2", "3", "4", "5", "6", "7", "8"], []);
  // Couleurs des barres (orange AGEROUTE pour "En Cours Travaux")
  const barColors = useMemo(() => ["#6c757d", "#fd7e14", "#ffc107", "#198754", "#dc3545", "#FF6D00", "#20c997", "#495057"], []);

  useEffect(() => {
    if (loading || totalAlertes === 0) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      return;
    }
    if (!chartRef.current) return;

    const labels = statutIds.map((k) => STATUT_LABELS[k] || k);
    const realValues = statutIds.map((k) => Number(stats[k]) || 0);
    const scaledData = realValues.map((v) => Math.round(v * HISTO_RATIO * 10) / 10);

    const data = {
      labels,
      datasets: [
        {
          label: "Nombre d'alertes",
          data: scaledData,
          backgroundColor: barColors,
          borderColor: barColors,
          borderWidth: 1,
        },
      ],
    };

    const config = {
      type: "bar" as const,
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context: { raw?: unknown }) => {
                const scaled = Number(context.raw ?? 0);
                const real = Math.round(scaled / HISTO_RATIO);
                return ` ${real} alerte(s)`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { stepSize: 25 },
          },
        },
      },
    };

    if (chartInstanceRef.current) {
      chartInstanceRef.current.data = data;
      chartInstanceRef.current.update();
    } else {
      chartInstanceRef.current = new Chart(chartRef.current, config);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [stats, loading, totalAlertes, HISTO_RATIO, barColors, statutIds]);

  // Cartes stats : icônes compactes et couleurs par statut
  const statCards = [
    { key: "1", label: "Non Affectée", icon: "ri-inbox-archive-line", color: "secondary" },
    { key: "2", label: "Non Demarrée", icon: "ri-timer-line", color: "warning" },
    { key: "3", label: "En Cours (Pause)", icon: "ri-pause-line", color: "info" },
    { key: "4", label: "Terminée", icon: "ri-check-double-line", color: "success" },
    { key: "5", label: "Arrêtée", icon: "ri-stop-line", color: "danger" },
    { key: "6", label: "En Cours (Travaux)", icon: "ri-hammer-line", color: "primary" },
    { key: "7", label: "Grand Public", icon: "ri-team-line", color: "secondary" },
    { key: "8", label: "Rejeté", icon: "ri-close-circle-line", color: "dark" },
  ];

  document.title = "Tableau de bord | Road Alert AGEROUTE";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Tableau de bord" pageTitle="Road Alert" />

          {/* En-tête du tableau de bord */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style={{ width: 56, height: 56, background: "rgba(255, 109, 0, 0.12)" }}
              >
                <i className="ri-dashboard-3-line fs-2 text-primary" />
              </div>
              <div>
                <h5 className="mb-0 fw-semibold" style={{ color: isDarkMode ? "#fff" : "#212529" }}>
                  Tableau de bord
                </h5>
                <p className="text-muted small mb-0">
                  Vue d’ensemble des alertes par statut
                </p>
              </div>
            </div>
            {!loading && (
              <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-3" style={{ background: isDarkMode ? "rgba(49, 27, 145, 0.15)" : "rgba(49, 27, 145, 0.08)" }}>
                <i className="ri-file-list-3-line fs-5" style={{ color: "#311B91" }} />
                <span className="fw-semibold" style={{ color: "#311B91" }}>{totalAlertes}</span>
                <span className="text-muted small">alerte{totalAlertes !== 1 ? "s" : ""} au total</span>
              </div>
            )}
          </div>

          {/* Filtres */}
          <FilterCard title="Filtres de recherche">
            <Row className="g-3 align-items-end">
                  <Col md={2}>
                    <Label className="small text-muted mb-1"><i className="ri-map-pin-line me-1" />Commune</Label>
                    {filtersLoading ? (
                      <div className="d-flex align-items-center">
                        <Spinner size="sm" color="primary" className="me-2" />
                        <span className="text-muted">Chargement...</span>
                      </div>
                    ) : (
                      <SearchableSelect
                        options={options.communeOptions}
                        value={filters.idCommune || null}
                        onChange={(value) => {
                          const newValue = value !== null && value !== undefined ? String(value) : "";
                          setFilters({ ...filters, idCommune: newValue });
                        }}
                        placeholder="--Tous--"
                      />
                    )}
                  </Col>
                  <Col md={2}>
                    <Label className="small text-muted mb-1"><i className="ri-flag-line me-1" />Priorité</Label>
                    {filtersLoading ? (
                      <div className="d-flex align-items-center">
                        <Spinner size="sm" color="primary" className="me-2" />
                        <span className="text-muted">Chargement...</span>
                      </div>
                    ) : (
                      <SearchableSelect
                        options={options.prioriteOptions}
                        value={filters.idPriorite || null}
                        onChange={(value) => {
                          const newValue = value !== null && value !== undefined ? String(value) : "";
                          setFilters({ ...filters, idPriorite: newValue });
                        }}
                        placeholder="--Tous--"
                      />
                    )}
                  </Col>
                  <Col md={2}>
                    <Label className="small text-muted mb-1"><i className="ri-list-check me-1" />Statut</Label>
                    {filtersLoading ? (
                      <div className="d-flex align-items-center">
                        <Spinner size="sm" color="primary" className="me-2" />
                        <span className="text-muted">Chargement...</span>
                      </div>
                    ) : (
                      <SearchableSelect
                        options={options.statutOptions}
                        value={filters.idStatutAlerte || null}
                        onChange={(value) => {
                          const newValue = value !== null && value !== undefined ? String(value) : "";
                          setFilters({ ...filters, idStatutAlerte: newValue });
                        }}
                        placeholder="--Tous--"
                      />
                    )}
                  </Col>
                    <Col md={2}>
                      <Label className="small text-muted mb-1"><i className="ri-calendar-line me-1" />Date début</Label>
                      <Input type="date" className="form-control rounded-3" value={filters.dateDebut} onChange={(e) => setFilters({ ...filters, dateDebut: e.target.value })} />
                    </Col>
                    <Col md={2}>
                      <Label className="small text-muted mb-1"><i className="ri-calendar-line me-1" />Date fin</Label>
                      <Input type="date" className="form-control rounded-3" value={filters.dateFin} onChange={(e) => setFilters({ ...filters, dateFin: e.target.value })} />
                    </Col>
                    <Col md={2}>
                      <Button className="w-100 btn-ageroute-orange rounded-pill shadow-sm" onClick={loadData}>
                        <i className="ri-search-line me-1" />Rechercher
                      </Button>
                    </Col>
                  </Row>
          </FilterCard>

              {/* Cartes stats - format compact */}
              <Row className="g-2 mb-4">
                {statCards.map(({ key, label, icon, color }) => (
                  <Col key={key} xl={3} md={4} sm={6}>
                    <Card className="card-stat-modern rounded-3 shadow-sm border-0 overflow-hidden h-100">
                      <CardBody className="p-2 px-3 py-2">
                        {loading ? (
                          <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 44 }}>
                            <Spinner size="sm" color="primary" />
                          </div>
                        ) : (
                          <div className="d-flex align-items-center">
                            <div className={`flex-shrink-0 rounded-2 d-flex align-items-center justify-content-center bg-${color} bg-opacity-25`} style={{ width: 36, height: 36 }}>
                              <i className={`${icon} text-${color}`} style={{ fontSize: "1rem" }} aria-hidden />
                            </div>
                            <div className="ms-2 flex-grow-1 min-w-0">
                              <p className="mb-0 text-truncate" style={{ fontSize: "0.75rem", color: isDarkMode ? "#adb5bd" : "#6c757d" }}>{label}</p>
                              <span className="fw-bold" style={{ fontSize: "1.1rem", color: isDarkMode ? "#fff" : "#212529" }}>{stats[key] ?? 0}</span>
                            </div>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                ))}
                <Col xl={3} md={4} sm={6}>
                  <Card className="card-stat-modern rounded-3 shadow-sm border-0 overflow-hidden card-ageroute h-100">
                    <CardBody className="p-2 px-3 py-2">
                      {loading ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 44 }}>
                          <Spinner size="sm" color="primary" />
                        </div>
                      ) : (
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0 rounded-2 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, backgroundColor: "rgba(49, 27, 145, 0.12)" }}>
                            <i className="ri-file-list-2-line" style={{ fontSize: "1rem", color: "#311B91" }} aria-hidden />
                          </div>
                          <div className="ms-2 flex-grow-1 min-w-0">
                            <p className="mb-0" style={{ fontSize: "0.75rem", color: isDarkMode ? "#adb5bd" : "#6c757d" }}>TOTAL</p>
                            <span className="fw-bold" style={{ fontSize: "1.1rem", color: "#311B91" }}>{totalAlertes}</span>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Graphique */}
              <Row>
                <Col xs={12}>
                  <Card className="card-ageroute rounded-3 shadow-sm border-0 overflow-hidden">
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 40, height: 40, background: "rgba(255, 109, 0, 0.12)" }}>
                          <i className="ri-bar-chart-grouped-line text-primary" style={{ fontSize: "1.25rem" }} />
                        </div>
                        <div>
                          <h5 className="card-title mb-0 fw-semibold">Répartition des alertes par statut</h5>
                          <p className="text-muted small mb-0">Échelle : 100 = 1 000 alertes</p>
                        </div>
                      </div>
                      {loading ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: 280 }}>
                          <Spinner color="primary" />
                          <span className="text-muted mt-2 small">Chargement des données...</span>
                        </div>
                      ) : totalAlertes > 0 ? (
                        <div style={{ height: 300, width: "100%" }} className="mt-3">
                          <canvas ref={chartRef} />
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <div
                            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                            style={{ width: 72, height: 72, background: isDarkMode ? "rgba(255,109,0,0.15)" : "rgba(255,109,0,0.1)" }}
                          >
                            <i className="ri-bar-chart-box-line text-primary" style={{ fontSize: "2rem" }} />
                          </div>
                          <p className="mb-0 fw-medium" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Aucune donnée à afficher</p>
                          <p className="text-muted small mt-1 mb-0">Modifiez les filtres et lancez une recherche.</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DashboardRoadAlert;
